import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Loader2, Download, AlertCircle, ChevronDown, Home } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  MetadataField,
  normaliseClaimField,
  resolveDependentOptions,
  isFieldVisible,
  isConditionallyRequired,
  isRequiredValueProvided,
  FILE_FIELD_TYPES,
} from '@/shared/lib/forms'
import { useCreateClaimMetadata } from '@/admin/hooks/use-claims'
import {
  useBulkFileClaims,
  type BulkClaimRow,
} from '@/admin/hooks/use-hoa-communities'
import { parseHoaClaimsFile } from '@/admin/lib/hoa-csv'
import api from '@/admin/lib/api'
import { FileDropzone } from '@/shared/components/ui/file-dropzone'
import { toastApiError } from '@/shared/lib/api-error'

/** A registered unit available to file a claim against. */
export interface BulkClaimUnit {
  uuid: string
  name: string
  holding_uuid: string
}

/**
 * Bulk-file unit claims for an HOA community under one umbrella (parent)
 * claim. The user selects units (or uploads a CSV of unit labels), fills the
 * shared claim details once, and one installed claim is filed per unit. The
 * umbrella HOA claim groups them all.
 */
export function BulkFileClaimsDialog({
  open,
  onOpenChange,
  communityUuid,
  communityName,
  units,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  communityUuid: string
  communityName: string
  units: BulkClaimUnit[]
}) {
  const navigate = useNavigate()
  const bulkClaims = useBulkFileClaims()

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(units.map((u) => u.uuid)),
  )
  // Units come pre-picked from the grid, so the list stays collapsed into a
  // summary by default - keeping the claim fields front and centre.
  const [unitsExpanded, setUnitsExpanded] = useState(false)
  // Show the "what's still needed" notice only after a submit attempt.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [values, setValues] = useState<Record<string, unknown>>({})
  // Optional per-unit description supplied via CSV (keyed by unit uuid).
  const [perUnitDescription, setPerUnitDescription] = useState<
    Record<string, string>
  >({})

  // Claim field metadata comes from the installed schema (the child claims).
  // Use the first unit's registration to resolve it; the schema is identical
  // across units.
  const firstHolding = units[0]?.holding_uuid ?? null
  const { data: metadata, isLoading: metadataLoading } = useCreateClaimMetadata(
    'installed',
    { holdingUuid: firstHolding },
  )
  const fields = metadata?.fields ?? []

  // Umbrella (hoa) schema metadata - for the intake "settlement preference"
  // field, sourced from config (no hardcoded option list).
  const { data: hoaMeta } = useCreateClaimMetadata('hoa')
  const settlementModeField = (hoaMeta?.fields ?? []).find(
    (f) => f.code_name === 'settlement_mode',
  )

  // Show a field if a visibility rule allows it OR the schema marks it
  // required. Some required fields (e.g. requestor, quantity_impacted) carry
  // visibility rules that hide them in the create context, yet the backend
  // still enforces them - so they must be captured here or the claim is
  // rejected. Required-but-hidden fields are surfaced so they can be filled.
  const visibleFields = fields.filter(
    (f) => isFieldVisible(f.configuration_data, values) || f.required,
  )
  // Bulk claims only collect simple inputs (no per-unit file uploads here).
  // FILE_FIELD_TYPES is a Set - use .has(), not .includes().
  const inputFields = visibleFields.filter(
    (f) => !FILE_FIELD_TYPES.has(f.field_type),
  )

  // The umbrella (hoa) claim requires a description, so force it here even
  // though it is optional on the installed child schema.
  const isRequired = (f: (typeof fields)[number]) =>
    f.required ||
    f.code_name === 'description' ||
    isConditionallyRequired(f.configuration_data, values)

  const missingRequired = visibleFields.filter(
    // `isRequiredValueProvided` treats a `false` acknowledgement checkbox as
    // unanswered (check-then-uncheck), unlike a plain emptiness test (DL-44).
    (f) => isRequired(f) && !isRequiredValueProvided(f, values[f.code_name]),
  )

  const selectedUnits = useMemo(
    () => units.filter((u) => selected.has(u.uuid)),
    [units, selected],
  )

  const handleChange = (codeName: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [codeName]: value }))
  }

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })

  const allSelected = units.length > 0 && selected.size === units.length
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(units.map((u) => u.uuid)))

  const downloadTemplate = async () => {
    // Pre-fill the template with this community's actual unit names so the
    // re-uploaded file matches every unit exactly.
    try {
      const res = await api.post(
        '/claims/bulk-file-claims/template/',
        { units: units.map((u) => u.name) },
        { responseType: 'blob' },
      )
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'community-claims-template.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toastApiError(err, 'Could not download the template')
    }
  }

  const handleCsv = async (file: File) => {
    const { rows, hasLabel } = await parseHoaClaimsFile(file)
    if (!hasLabel) {
      toast.error('No "Unit" column found in the file.')
      return
    }
    const byLabel = new Map(units.map((u) => [u.name.trim().toLowerCase(), u]))
    const matched = new Set<string>()
    const descriptions: Record<string, string> = {}
    const unmatched: string[] = []
    for (const row of rows) {
      const unit = byLabel.get(row.label.trim().toLowerCase())
      if (unit) {
        matched.add(unit.uuid)
        if (row.description) descriptions[unit.uuid] = row.description
      } else {
        unmatched.push(row.label)
      }
    }
    if (matched.size === 0) {
      // Don't wipe the user's existing selection when nothing matched -
      // explain why so they can fix the unit names (or use the Template).
      toast.error(
        `None of the rows matched a unit in this community. ` +
          `Unit names must match exactly (e.g. "${units[0]?.name ?? 'Unit 1'}"). ` +
          `Tip: download the Template - it's pre-filled with this community's units.`,
      )
      return
    }
    setSelected(matched)
    setPerUnitDescription(descriptions)
    toast.success(
      `Matched ${matched.size} of ${rows.length} row${rows.length !== 1 ? 's' : ''}` +
        (unmatched.length
          ? ` - ${unmatched.length} not found: ${unmatched.slice(0, 5).join(', ')}`
          : ''),
    )
  }

  const handleSubmit = async () => {
    if (selectedUnits.length === 0 || missingRequired.length > 0) {
      // Surface a gentle inline notice (not a red error) and reveal the units
      // list if that's what's missing, so the user can see what to complete.
      setAttemptedSubmit(true)
      if (selectedUnits.length === 0) setUnitsExpanded(true)
      return
    }
    const rows: BulkClaimRow[] = selectedUnits.map((u) => ({
      holding_uuid: u.holding_uuid,
      attribute_values: perUnitDescription[u.uuid]
        ? { description: perUnitDescription[u.uuid] }
        : undefined,
    }))
    try {
      const res = await bulkClaims.mutateAsync({
        community_uuid: communityUuid,
        parent_attribute_values: values,
        shared_attribute_values: values,
        rows,
      })
      toast.success(
        `${res.created} unit claim${res.created !== 1 ? 's' : ''} filed` +
          (res.skipped ? ` (${res.skipped} skipped)` : ''),
      )
      onOpenChange(false)
      // Land on Overview for a freshly created claim (not the work queue tab).
      navigate(`/claims/${res.parent_uuid}?tab=overview`)
    } catch (err) {
      toastApiError(err, 'Failed to file bulk claims')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[94vw] sm:max-w-[880px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            File a claim for {communityName}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            One HOA claim is created, with a unit claim filed for each selected
            home. Fill the shared details once - they apply to every unit.
          </DialogDescription>
        </DialogHeader>

        <div className="clara-scroll flex max-h-[64vh] flex-col gap-4 overflow-y-auto pr-1">
          {/* Gentle "what's still needed" notice - only after a submit try */}
          {attemptedSubmit &&
            (missingRequired.length > 0 || selectedUnits.length === 0) && (
              <div className="flex items-start gap-2 rounded-[8px] border-[0.5px] border-[var(--amber-rule,var(--rule))] bg-[var(--amber-soft,var(--bg-subtle))] px-3 py-2.5 text-[12px] text-[var(--ink-2)]">
                <AlertCircle
                  size={14}
                  className="mt-[1px] shrink-0 text-[var(--amber,#b45309)]"
                />
                <span>
                  Just a few things left to complete:{' '}
                  <span className="text-[var(--ink)]">
                    {[
                      selectedUnits.length === 0 ? 'select at least one home' : '',
                      ...missingRequired.map((f) => f.display_name),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    .
                  </span>
                </span>
              </div>
            )}

          {/* Units - collapsed into a summary by default (picked on the grid) */}
          {units.length === 0 ? (
            <p className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
              No registered units in this community yet. Register units first,
              then file claims.
            </p>
          ) : (
            <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
              <Button
                variant="ghost"
                onClick={() => setUnitsExpanded((v) => !v)}
                className="flex h-auto w-full cursor-pointer items-center justify-between gap-2 rounded-none px-3 py-2.5 text-left font-normal hover:bg-[var(--bg)]"
              >
                <span className="flex items-center gap-2 text-[12px] text-[var(--ink)]">
                  <Home size={13} className="text-[var(--ink-3)]" />
                  Filing for{' '}
                  <span className="font-medium">
                    {selectedUnits.length} home
                    {selectedUnits.length !== 1 ? 's' : ''}
                  </span>
                  <span className="text-[var(--ink-3)]">of {units.length}</span>
                </span>
                <span className="flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                  {unitsExpanded ? 'Hide' : 'Change selection'}
                  <ChevronDown
                    size={13}
                    className={unitsExpanded ? 'rotate-180' : ''}
                  />
                </span>
              </Button>
              {unitsExpanded && (
                <div className="border-t-[0.5px] border-[var(--rule)]">
                  <div className="flex items-center justify-between px-3 py-2">
                    <Button
                      variant="ghost"
                      onClick={toggleAll}
                      className="flex h-auto cursor-pointer items-center gap-2 p-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
                    >
                      <Checkbox checked={allSelected} />
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={downloadTemplate}
                      className="cursor-pointer gap-1 text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                    >
                      <Download size={12} />
                      Template
                    </Button>
                  </div>
                  <div className="px-3 pb-3">
                    <FileDropzone
                      accept=".csv,.xlsx,.xls,text/csv"
                      onFile={(f) => void handleCsv(f)}
                      hint="CSV or Excel - matches homes by name, optional Description column"
                    />
                  </div>
                  <div className="clara-scroll max-h-[180px] overflow-y-auto border-t-[0.5px] border-[var(--rule)]">
                    {units.map((u) => (
                      <Button
                        key={u.uuid}
                        variant="ghost"
                        onClick={() => toggle(u.uuid)}
                        className="flex h-auto w-full cursor-pointer items-center justify-start gap-2 rounded-none border-b-[0.5px] border-[var(--rule)] px-3 py-2 font-normal last:border-b-0 hover:bg-[var(--bg)]"
                      >
                        <Checkbox checked={selected.has(u.uuid)} />
                        <Home size={12} className="text-[var(--ink-3)]" />
                        <span className="text-[12px] text-[var(--ink)]">
                          {u.name}
                        </span>
                        {perUnitDescription[u.uuid] && (
                          <span className="ml-auto max-w-[220px] truncate text-[11px] text-[var(--ink-3)]">
                            {perUnitDescription[u.uuid]}
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Shared claim details */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Claim details (applied to every home)
              </Label>
              <span className="text-[11px] text-[var(--ink-3)]">
                <span className="text-[var(--red)]">*</span> required
              </span>
            </div>
            {metadataLoading ? (
              <p className="py-4 text-[13px] text-[var(--ink-3)]">
                Loading claim fields...
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {settlementModeField && (
                  <div className="sm:col-span-2">
                    <MetadataField
                      field={normaliseClaimField(settlementModeField)}
                      value={values.settlement_mode}
                      onChange={(v) => handleChange('settlement_mode', v)}
                      labelVariant="compact"
                    />
                  </div>
                )}
                {inputFields.map((field) => {
                  const normalised = normaliseClaimField(field)
                  normalised.required = isRequired(field)
                  const depOptions = resolveDependentOptions(
                    field.configuration_data,
                    values,
                  )
                  if (depOptions) normalised.options = depOptions
                  // Cascading field whose parent isn't chosen yet: rather than
                  // lock it, offer the full combined option list so it's always
                  // usable. Once the parent IS picked, the list narrows to that
                  // group (handled by resolveDependentOptions above).
                  const cfg = field.configuration_data as
                    | {
                        depends_on?: string
                        option_groups?: Record<
                          string,
                          { label: string; value: string }[]
                        >
                      }
                    | undefined
                  if (
                    cfg?.depends_on &&
                    cfg.option_groups &&
                    !values[cfg.depends_on]
                  ) {
                    const seen = new Set<string>()
                    const union: { label: string; value: string }[] = []
                    for (const grp of Object.values(cfg.option_groups)) {
                      if (!Array.isArray(grp)) continue
                      for (const opt of grp) {
                        if (opt && !seen.has(opt.value)) {
                          seen.add(opt.value)
                          union.push(opt)
                        }
                      }
                    }
                    if (union.length > 0) normalised.options = union
                  }
                  // Long-form fields span the full width for readability.
                  const wide =
                    field.field_type === 'RICH_TEXT' ||
                    field.field_type === 'TEXTAREA' ||
                    field.code_name === 'description' ||
                    field.code_name === 'customer_statement'
                  return (
                    <div
                      key={field.code_name}
                      className={wide ? 'sm:col-span-2' : ''}
                    >
                      <MetadataField
                        field={normalised}
                        value={values[field.code_name]}
                        onChange={(v) => handleChange(field.code_name, v)}
                        labelVariant="compact"
                      />
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={bulkClaims.isPending || selectedUnits.length === 0}
            className="cursor-pointer gap-1.5"
          >
            {bulkClaims.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            File {selectedUnits.length} claim
            {selectedUnits.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
